import '@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css';
import { LicenseInfo } from '@mui/x-data-grid-pro';
import 'devextreme/dist/css/dx.light.css'; // TODO: remove it
import { UserManager } from 'oidc-client';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { loadUser, OidcProvider } from 'redux-oidc';
import { getIdentityClientSettingsAsync } from './apiClients/SettingsClient';
import LoadingScreen from './components/commonComponents/LoadingScreen';
import NotistackProvider from './components/commonComponents/NotistackProvider';
// components
import RtlLayout from './components/commonComponents/RtlLayout';
import ThemeLocalization from './components/commonComponents/ThemeLocalization';
import ThemePrimaryColor from './components/commonComponents/ThemePrimaryColor';
import { MUI_X_LICENSE } from './config';
import { endFetchUserInformation, initalizeFetchUserInformation, updateUserInformation } from './redux/reducersAndActions/userInfoActionsAndReducer';
import { UpdateUserInfoActionPayload } from './redux/reducersAndActionsType/userInfoActionAnReducerTypes';
// redux
import { store, useDispatch } from './redux/store';
// routes
import Router from './routes';
// theme
import ThemeConfig from './theme';
import { getBackendDataListIDM, setClientOptions } from './utils/backendDataHelper';
import { getSelectedClient, saveIdentityClientSettings } from './utils/localStorage';
import createCompanySpecificUserManager from './utils/userManagerHelper';
import withButtonTracking from './hooks/useButtonTracking';
import { appInsights, hasSentDailyLog, updateLastSentDate } from './hooks/useAppinsight';
import { AuthMethod } from './common/enums';
import { getUserInfoAsync } from './apiClients/UserInfoClient';
import { IdentityClientSettings } from '@gsgroup/gsgroup-react-ui';
import { BackendData } from './utils/backendDataParser';
import React from 'react';
import { useSnackbar } from 'notistack';

LicenseInfo.setLicenseKey(MUI_X_LICENSE);

// ----------------------------------------------------------------------

const StatefulApp: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [identityClientSettings, setIdentityClientSettings] = useState<IdentityClientSettings>();
  const [userManager, setUserManager] = useState<UserManager>();
  const [userFinishedLoading, setUserFinishedLoading] = useState(false);

  useEffect(() => {
    getIdentityClientSettingsAsync()
      .then(settings => {
        setIdentityClientSettings(settings);
      }).catch((error: Error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      })
  }, []);

  useEffect(() => {
    if (identityClientSettings) {
      saveIdentityClientSettings(identityClientSettings);

      const userManager = createCompanySpecificUserManager(identityClientSettings);

      setUserManager(userManager);
    }
  }, [identityClientSettings]);

  useEffect(() => {
    if (userManager && !userFinishedLoading) {
      dispatch(initalizeFetchUserInformation());

      loadUser(store, userManager)
        .then(async (reloadedUser) => {
          if (reloadedUser === undefined || reloadedUser === null) {
            console.log('App | Reloaded user is undefined.');

            setUserFinishedLoading(true);
            dispatch(endFetchUserInformation());
          }
          else {
            console.log('App | User found.');
            console.log(`App | access_token: ${reloadedUser.access_token}`);
            console.log(`App | expired: ${reloadedUser.expired}`);
            console.log(`App | expires_at: ${reloadedUser.expires_at}`);
            console.log(`App | expires_in: ${reloadedUser.expires_in}`);

            let selectedClient: BackendData | undefined = undefined;

            if (identityClientSettings?.authMethod === AuthMethod.IDM) {
              const backendDataList = getBackendDataListIDM(reloadedUser);
              selectedClient = setClientOptions(backendDataList);
            } else {
              //Selected Client should either come from Hm office's embedding logic
              //Or developers can locally use /Admin page to set the selected client
              selectedClient = getSelectedClient();
            }

            if (!hasSentDailyLog()) {
              const eventData = {
                name: 'UserLogin',
                properties: {
                  application: 'Web office',
                  client: selectedClient?.name,
                  user: reloadedUser.profile.sub,
                }
              };
              console.log('Sending daily use count event:', eventData);
              appInsights.trackEvent(eventData);
              updateLastSentDate();
            }

            if (reloadedUser.expired) {
              console.log(`App | User expired at: ${reloadedUser.expires_at}`);

              // Removing stale user record, so the auto-login process can restart
              userManager.removeUser();

              setUserFinishedLoading(true);
              dispatch(endFetchUserInformation());
            }
            else if (!selectedClient) {
              console.log('App | Selected Client is undefined.');

              setUserFinishedLoading(true);
              dispatch(endFetchUserInformation());
            }
            else {
              getUserInfoAsync(reloadedUser.access_token, selectedClient.name)
                .then(result => {
                  const payload: UpdateUserInfoActionPayload = {
                    userInformation: result
                  };

                  dispatch(updateUserInformation(payload));

                  console.log('App | User information update dispatched.');
                })
                .catch((error) => {
                  console.error('Error fetching user information:', error);
                  dispatch(endFetchUserInformation());
                  navigate('/error');
                })
                .finally(() => {
                  setUserFinishedLoading(true);
                });
            }
          }
        })
        .catch((error) => {
          console.error('Error loading user:', error);
          setUserFinishedLoading(true);
          dispatch(endFetchUserInformation());
        });
    }
  }, [userManager]);

  return (
    <>
      {
        userManager !== undefined && userFinishedLoading === true ?
          /* @ts-ignore */ /* ignore needed because in react 18.x children prop was removed */
          <OidcProvider userManager={userManager} store={store}>
            <Router />
          </OidcProvider>
          :
          <LoadingScreen />
      }
    </>
  );
};

const TrackedApp = withButtonTracking(StatefulApp);

export default function App() {
  return (
    <ThemeConfig>
      <ThemePrimaryColor>
        <ThemeLocalization>
          <RtlLayout>
            <NotistackProvider>
              <TrackedApp />
              {/* <Settings /> */}
            </NotistackProvider>
          </RtlLayout>
        </ThemeLocalization>
      </ThemePrimaryColor>
    </ThemeConfig>
  );
}
